import React, { useState, useEffect } from "react";
import { Modal, Button, Upload, Typography, message, Input, Spin } from "antd";
import {
  UploadOutlined,
  CheckCircleTwoTone,
  DownloadOutlined,
  DeleteOutlined,
  UploadOutlined as UploadIcon,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { bulkOrderSaga } from "./saga";
import { selectLoading, selectError, selectUploadSuccess } from "./selectors";
import templateFile from "../../../../../../../templates/bulk_order_template.csv";

const { Text } = Typography;

export default function BulkOrderUpload({ onBulkSuccess }) {
  const dispatch = useDispatch();

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: bulkOrderSaga });

  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const uploadSuccess = useSelector(selectUploadSuccess);

  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    if (uploadSuccess && visible) {
      message.success({
        content:
          "Bulk order processing started! You'll receive an email notification once complete.",
        duration: 20,
      });
      handleClose();
      onBulkSuccess?.();
    }
  }, [uploadSuccess, visible]);

  useEffect(() => {
    if (error && visible) {
      const errorData = error?.data || error?.response?.data?.data;
      if (errorData && errorData.errors && Array.isArray(errorData.errors)) {
        message.error({
          content: (
            <div>
              <div style={{ fontWeight: "bold", marginBottom: 8 }}>
                Validation failed with {errorData.errorCount} error(s):
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {errorData.errors.slice(0, 5).map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
                {errorData.errors.length > 5 && (
                  <li>...and {errorData.errors.length - 5} more errors</li>
                )}
              </ul>
            </div>
          ),
          duration: 10,
        });
      } else {
        message.error(` ${error}`);
      }
    }
  }, [error, visible]);

  const validateFile = (file) => {
    const isCSV = file.type === "text/csv";

    if (!isCSV) {
      message.error(" Only CSV files are allowed.");
      return false;
    }

    return true;
  };

  const uploadProps = {
    accept: ".csv",
    multiple: false,
    beforeUpload: (selectedFile) => {
      if (!validateFile(selectedFile)) return Upload.LIST_IGNORE;
      setFile(selectedFile);
      return false;
    },
    showUploadList: false,
  };

  const handleUpload = () => {
    if (!file) {
      message.error("Please select a file to upload");
      return;
    }
    window.__bulkOrderFile = file;
    dispatch(actions.uploadBulkOrder({ email }));
  };

  const handleClose = () => {
    setVisible(false);
    setFile(null);
    setEmail("");
    setEmailError(null);
    dispatch(actions.resetUploadState());
  };

  return (
    <>
      {/* Trigger Button */}
      <Button
        onClick={() => setVisible(true)}
        icon={<UploadIcon />}
        style={{
          borderRadius: 6,
          padding: "0 14px",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        Bulk Order Creation
      </Button>

      <Modal
        visible={visible}
        onCancel={handleClose}
        title="Bulk Order Creation"
        width={650}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={loading} tip="Uploading...">
          <Text type="secondary">
            Upload a CSV file to create multiple orders at once
          </Text>

          {/* Download Template */}
          <div
            style={{
              border: "1px solid #E6E8EF",
              borderRadius: 8,
              padding: 16,
              marginTop: 20,
              background: "#F7F9FC",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Text strong>Download Template</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Use this template to format your data.
              </Text>
            </div>

            <Button
              icon={<DownloadOutlined />}
              onClick={() => {
                const link = document.createElement("a");
                link.href = templateFile;
                link.download = "Bulk_Order_Template.csv";
                link.click();
              }}
            >
              Download
            </Button>
          </div>

          {/* File Section */}
          <div
            style={{
              border: file ? "2px solid #52C41A" : "2px dashed #D9D9D9",
              borderRadius: 10,
              padding: 35,
              marginTop: 25,
              textAlign: "center",
              background: file ? "#F6FFED" : "transparent",
            }}
          >
            {!file ? (
              <>
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
                <p style={{ marginTop: 10, color: "#8c8c8c" }}>
                  Only CSV format is allowed
                </p>
              </>
            ) : (
              <>
                <CheckCircleTwoTone
                  twoToneColor="#52C41A"
                  style={{ fontSize: 40 }}
                />
                <div style={{ marginTop: 10 }}>
                  <Text strong>{file.name}</Text>
                  <br />
                  <Text type="secondary">
                    {(file.size / 1024).toFixed(2)} KB
                  </Text>
                </div>

                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setFile(null)}
                  style={{ marginTop: 12 }}
                >
                  Remove
                </Button>
              </>
            )}
          </div>

          <div style={{ marginTop: 20 }}>
            <Text strong>Notification Email</Text>
            <Input
              placeholder="Enter email address to receive status updates"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              status={emailError ? "error" : ""}
              style={{ marginTop: 6 }}
              disabled={loading}
            />
            {emailError && (
              <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>
                {emailError}
              </div>
            )}
          </div>

          {/* Note */}
          <div
            style={{
              background: "#E9F3FF",
              padding: 12,
              borderRadius: 6,
              marginTop: 20,
            }}
          >
            <Text>
              <strong>Note:</strong> Bulk order creation will happen in the
              background. You'll be notified once processed.
            </Text>
          </div>

          {/* Footer */}
          <div style={{ textAlign: "right", marginTop: 25 }}>
            <Button
              onClick={handleClose}
              style={{ marginRight: 12 }}
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              disabled={!file || !email || !!emailError || loading}
              onClick={handleUpload}
              loading={loading}
            >
              Upload & Create Orders
            </Button>
          </div>
        </Spin>
      </Modal>
    </>
  );
}
